import { alpha, Box, CircularProgress, useTheme } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactECharts from "echarts-for-react";
import useStyles from "./styles";
import * as dayjs from "dayjs";
import LegendsCharts from "../legends";
import { convertBitsToBytes } from "../../../../utils/easyMaps/convertToBytes";
import { getColorsArray } from "./utils/getColorsPallet";

const LineChart = (props) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const [boxHeight, setBoxHeight] = useState(null);
  const [boxWidth, setBoxWidth] = useState(null);
  const lineRef = useRef(null);
  const chartRef = useRef(null);
  const colorMode = theme.palette.mode;
  const {
    groupData,
    unit,
    method,
    isMultiple,
    setFilterState,
    enlaceSelected,
    isPotencia,
    colorOffset,
    loading,
  } = props;

  useEffect(() => {
    const element = lineRef.current;
    if (!element) return;

    let resizeTimer;
    const resizeObserver = new ResizeObserver((entries) => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length) return;
          const { width, height } = entries[0].contentRect;
          if (width > 0 && height > 0) {
            setBoxWidth(width);
            setBoxHeight(height);
          }
        });
      }, 50);
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
      clearTimeout(resizeTimer);
    };
  }, []);

  const { formattedData, chartColors } = useMemo(() => {
    if (!groupData) return { formattedData: [], allRawValues: [] };

    const colors = getColorsArray(groupData.length, colorMode, colorOffset);

    const formatted = groupData.map((series, index) => {
      const points = series.data.map((item) => {
        const isoDate = dayjs(item.clock * 1000).toISOString();

        return [isoDate, item.value];
      });
      return {
        name: series.label,
        points: points,
        color: colors[index],
      };
    });

    return { formattedData: formatted, chartColors: colors };
  }, [groupData, colorMode, colorOffset]);

  const handleZoom = useCallback(() => {
    if (chartRef.current && formattedData.length > 0) {
      const chartInstance = chartRef.current.getEchartsInstance();
      const option = chartInstance.getOption();

      if (!option.dataZoom || !option.dataZoom.length) return;

      const timeStart = option.dataZoom[0].startValue;
      const timeEnd = option.dataZoom[0].endValue;
      if (timeStart !== undefined && timeEnd !== undefined) {
        const start = dayjs(timeStart);
        const end = dayjs(timeEnd);

        const diffInMinutes = end.diff(start, "minute");

        // Controle para não realizar recortes em tempos menores que 1 minuto
        if (diffInMinutes >= 1) {
          setFilterState({
            timeStart: timeStart,
            timeEnd: timeEnd,
          });
        } else {
          chartInstance.dispatchAction({
            type: "dataZoom",
            start: 0,
            end: 100,
          });
        }
      }
    }
  }, [formattedData, setFilterState]);

  const onEvents = useMemo(
    () => ({
      dataZoom: handleZoom,
    }),
    [handleZoom],
  );

  const handleChartReady = useCallback((chart) => {
    chart.dispatchAction({
      type: "takeGlobalCursor",
      key: "dataZoomSelect",
      dataZoomSelectActive: true,
    });
  }, []);

  const chartOption = useMemo(() => {
    if (formattedData.length === 0) return {};

    const firstSeriesLen = formattedData[0]?.points?.length || 1;
    const showPoint = (boxWidth || 1) / firstSeriesLen > 15;

    const series = formattedData.map((s) => ({
      name: s.name,
      type: "line",
      symbol: showPoint ? "circle" : "none",
      symbolSize: 8,
      areaStyle: isMultiple ? null : { opacity: 0.5 },
      data: s.points,
    }));

    return {
      animation: true,
      color: chartColors,
      grid: { top: 20, right: 12, bottom: 0, left: 12, containLabel: true },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "line" },
        backgroundColor: theme.palette.background.default,
        textStyle: { color: theme.palette.text.primary },
        confine: true,
        extraCssText:
          "width: 300px; white-space: normal; word-break: break-word;",
        formatter: function (params) {
          if (!params || params.length === 0) return "";

          const maxVisible = 5;

          const formattedTime = dayjs(Number(params[0].axisValue)).format(
            "DD/MM/YYYY HH:mm:ss",
          );

          const sortedParams = [...params].sort((a, b) => {
            const valA = Array.isArray(a.value) ? a.value[1] : a.value;
            const valB = Array.isArray(b.value) ? b.value[1] : b.value;
            return valB - valA;
          });

          let showParams = sortedParams.slice(0, maxVisible);
          let html = `<b style="font-size: 12px;">${formattedTime}</b><br/>`;

          showParams.forEach((item) => {
            const val = Array.isArray(item.value) ? item.value[1] : item.value;
            const actualValue = formatValue(val, unit, item.seriesName);

            html += `${item.marker} ${item.seriesName}: <b>${actualValue}</b><br/>`;
          });

          if (params.length > maxVisible) {
            html += `<div style="font-size: 10px; color: #888; margin-top: 5px;">
      + ${params.length - maxVisible} outras séries
    </div>`;
          }

          return html;
        },
      },
      legend: { show: false },
      xAxis: {
        type: "time",
        boundaryGap: false,
        axisTick: { show: true },
        axisLine: { show: false },
        splitLine: {
          show: true,
          lineStyle: {
            type: "dashed",
            color: theme.palette.divider,
            opacity: 0.5,
          },
        },
        axisLabel: {
          formatter: (value) => dayjs(value).format("HH:mm:ss"),
          fontSize: 10,
          color: theme.palette.text.primary,
          hideOverlap: true,
        },
      },
      toolbox: {
        feature: {
          dataZoom: { icon: null, yAxisIndex: "none" },
          restore: { icon: null },
          saveAsImage: { icon: null },
        },
      },
      dataZoom: [
        {
          type: "inside",
          zoomOnMouseWheel: false,
          moveOnMouseMove: false,
          moveOnMouseWheel: false,
          preventDefaultMouseMove: false,
        },
      ],
      yAxis: {
        type: "value",
        min: (value) => {
          if (unit == "%") {
            return 0;
          }
          const padding = (value.max - value.min) * 0.1;
          return value.min - padding;
        },
        max: (value) => {
          if (unit == "%") {
            return 100;
          }
          const padding = (value.max - value.min) * 0.1;
          return value.max + padding;
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: "dashed",
            color: theme.palette.divider,
            opacity: 0.5,
          },
        },
        axisLabel: {
          formatter: (value) => {
            const labelContext = formattedData[0]?.name || "";
            return formatValue(value, unit, labelContext);
          },
          fontSize: 10,
          color: theme.palette.text.primary,
        },
      },
      series,
    };
  }, [boxWidth, theme, formattedData, isMultiple, unit]);

  const formatValue = (value, unit, itemName) => {
    if (value === null || value === undefined || isNaN(value)) return value;

    if (enlaceSelected) {
      const itemNameisPot = itemName?.toLowerCase().includes("potência");
      if (itemNameisPot) {
        return `${Number(value).toFixed(2)} dbm`;
      } else {
        if (value == 0) {
          return "0";
        }
        const { valor, unidade } = convertBitsToBytes(value);
        return `${valor} ${unidade}`.trim();
      }
    }

    switch (unit) {
      case "%":
        return `${value}%`;
      case "ms":
        return `${(value * 1000).toFixed(2)} ms`;
      case "number":
        return value;
      default:
        return `${value}${unit ? ` ${unit}` : ""}`;
    }
  };
  return (
    <Box sx={styles.boxContainer}>
      <Box
        sx={{
          ...styles.boxLegend,
          minHeight: "30px",
          flexShrink: 0,
        }}
      >
        <LegendsCharts
          keys={[...formattedData.flatMap((d) => d.name)]}
          colors={chartColors}
        />
      </Box>
      <Box
        sx={{
          ...styles.boxContainerSizeChart,
          overflow: "hidden",
          minWidth: 0,
          width: "100%",
          flexGrow: 1,
          height: "100%",
          position: "relative",
        }}
        ref={lineRef}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <ReactECharts
            ref={chartRef}
            option={chartOption}
            style={{ height: "100%", width: "100%" }}
            notMerge={false}
            lazyUpdate={true}
            autoResize={true}
            onChartReady={handleChartReady}
            onEvents={onEvents}
          />
        </Box>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.background.cardStatus, 0.6),
              zIndex: 2,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LineChart;
